import type { FormEvent, ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useStreamFunctions } from 'stream/globalFunction/useStreamFunctions';

import type { CreateFunctionCommand as CreateFunctionPayload } from '@polyai/schemas';
import { AddIcon } from '@polyai/ui/components/atoms/Icons';
import Input from '@polyai/ui/components/atoms/Input';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';
import { PopupMenuItem } from '@polyai/ui/components/molecules/PopupMenu';

import type { FunctionType } from 'api/types';
import { generateFunctionId } from 'lib/prettyUuid';
import { validateStreamFunctionName } from 'utils/nameValidators';
import { generateDefaultFunctionCode } from '../../../RichTextInput/utils';
import { createFunctionButton } from './CreateFunctionButton.copy';

interface CreateFunctionButtonProps {
  defaultName?: string;
  onCreate?: (funcName: string, id: string) => void;
  functionType?: FunctionType;
  description?: string;
  code?: string;
  title?: string;
  subtitle?: string;
  trigger?: ReactElement;
  isOpen?: boolean;
}

const CreateFunctionButton = ({
  defaultName,
  onCreate,
  functionType,
  description = '',
  code = undefined,
  title = createFunctionButton.title,
  subtitle = createFunctionButton.subtitle,
  trigger,
  isOpen,
}: CreateFunctionButtonProps) => {
  const [functionName, setFunctionName] = useState(defaultName);
  const [error, setError] = useState('');

  const { activeFunctions, createFunction } = useStreamFunctions();

  const defaultTrigger = (
    <PopupMenuItem icon={<AddIcon size="sm" />} isCreate>
      Create function
    </PopupMenuItem>
  );

  useEffect(() => {
    if (!functionName) {
      return;
    }

    const error = validateStreamFunctionName(activeFunctions)(functionName);

    if (error !== true) {
      setError(error);
      return;
    }
    setError('');
  }, [activeFunctions, functionName]);

  const handleCreate = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!functionName) {
        return;
      }

      const payload: CreateFunctionPayload = {
        id: generateFunctionId(),
        name: functionName,
        description,
        code: generateDefaultFunctionCode({
          functionName,
          functionType,
          code,
        }),
        references: {
          flowSteps: {},
          topics: {},
          behaviour: {},
          stopKeywords: {},
          variables: {},
        },
        parameters: [],
        latencyControl: {
          enabled: false,
          delayResponses: [],
          initialDelay: 0,
          interval: 0,
        },
        errors: [],
        archived: false,
      };

      void createFunction(payload);
      onCreate?.(functionName, payload.id);
    },
    [functionName, code, onCreate, description, createFunction, functionType],
  );

  const contentToRender = (
    <form onSubmit={handleCreate}>
      <Input
        data-test-id="function-name-input-btn"
        disabled={!!defaultName}
        errors={error ? [error] : undefined}
        label="Function name"
        placeholder="Type in"
        value={functionName}
        fullWidth
        onChange={e => setFunctionName(e.target.value)}
      />
    </form>
  );

  return (
    <ConfirmationModal
      buttonText="Create"
      cancelText="Cancel"
      confirmDisabled={!functionName || !!error}
      confirmRequesting={false}
      content={contentToRender}
      open={isOpen}
      size="md"
      subtitle={subtitle}
      title={title}
      trigger={trigger ?? defaultTrigger}
      skipCloseAndConfirm
      onConfirmButtonClick={handleCreate}
    />
  );
};

export default CreateFunctionButton;
